import { cloneElement, isValidElement, type ComponentProps, type ReactElement } from 'react';

import { clsxm, type ClassValue } from '@vs-shop/utils';

/**
 * The `render` escape: a part renders a neutral element of its own, or paints
 * the element the page hands it instead.
 *
 * A shared part cannot choose whether the text it carries is a heading, or at
 * what level, or whether pressing it navigates or opens something — those are
 * the page's decisions. So it takes the element the page wants —
 * `render={<Heading level={1} />}`, `render={<Link href={url} />}` — as a
 * template: the supplied element contributes the tag and whatever it was given,
 * the part contributes the copy, the recipe and the slot.
 *
 * It lives in the primitive tier because both tiers need it and the mechanism
 * carries no commerce meaning, and it is a subpath rather than package-internal
 * for the same reason: `@vs-shop/commerce-ui` reaches it across a package
 * boundary. Sections import it through that package's own
 * `internal/renderedPart.tsx`, which is the line a tenant does not write past.
 *
 * Not Base UI's `useRender`, which answers the same question one tier up but is
 * a HOOK: the sections composing this are server components, where a hook
 * cannot run.
 */

/**
 * An element this can paint through, declared by what is read off it rather
 * than by a tag — so any element with a `className` qualifies, whether it is a
 * host element or a component like `Heading`.
 */
export type RenderElement = ReactElement<{ className?: ClassValue }>;

/** What a rendered part needs beyond the native props of whatever it renders. */
type RenderedPartFields = {
  /** The slot the part is reachable by, whichever element ends up rendered. */
  readonly dataSlot: string;
  /**
   * The part's recipe already merged with the caller's `className`. Resolved by
   * the part rather than here, because the recipe is the part's own.
   */
  readonly className: string;
  /** Required, and `undefined` when the page wants the neutral element. */
  readonly render: RenderElement | undefined;
};

export type RenderedPartProps = ComponentProps<'div'> & RenderedPartFields;

/**
 * Paint the supplied element with the part's slot, recipe and remaining props,
 * or answer `null` when the part is to render its own neutral element.
 *
 * Split out from `RenderedPart` because the neutral element differs per part —
 * a div for one that carries text, a `<button>` for one that carries an action —
 * while the cloning is the same everywhere.
 */
export const renderThrough = (
  render: RenderElement | undefined,
  dataSlot: string,
  className: string,
  props: Record<string, unknown>
): ReactElement | null => {
  if (!isValidElement(render)) {
    return null;
  }

  // Named rather than passed inline: the element's declared props are only
  // what is read off it (`className`), while what it hands back is the whole
  // prop bag. An object literal would be excess-property-checked against the
  // narrower type and rejected.
  //
  // The supplied element's own class merges LAST, so a page writing
  // `render={<h1 className="uppercase" />}` beats both the part's recipe and
  // whatever the primitive it names paints by default.
  //
  // A `data-slot` the CALL SITE passed wins over the part's own, which is what
  // the neutral branch already does by spreading `props` after the attribute.
  // A tenant naming a slot is naming the thing it composed, and a route's
  // assertions follow that name rather than the part's.
  const clonedProps = {
    ...props,
    className: clsxm(className, render.props.className),
    'data-slot': props['data-slot'] ?? dataSlot,
  };

  return cloneElement(render, clonedProps);
};

export const RenderedPart = ({ dataSlot, className, render, ...props }: RenderedPartProps) =>
  renderThrough(render, dataSlot, className, props) ?? (
    <div data-slot={dataSlot} className={className} {...props} />
  );

export type RenderedInlinePartProps = ComponentProps<'span'> & RenderedPartFields;

/**
 * The same thing for a part whose neutral element is inline.
 *
 * A separate export rather than an option on `RenderedPart`, because the choice
 * is a fact of the part and not a decision its caller makes — and because the
 * two carry genuinely different prop surfaces: an event handler declared for a
 * `div` is not one declared for a `span`.
 */
export const RenderedInlinePart = ({
  dataSlot,
  className,
  render,
  ...props
}: RenderedInlinePartProps) =>
  renderThrough(render, dataSlot, className, props) ?? (
    <span data-slot={dataSlot} className={className} {...props} />
  );
